/**
 * Normalizes API errors into a human-readable message.
 *
 * The backend returns Zod validation failures as:
 *   { ok: false, message: "Validation failed.", errors: { formErrors, fieldErrors } }
 *
 * `message` alone ("Validation failed.") is not actionable, so when an `errors`
 * payload is present we flatten the field/form errors into the message instead.
 */

type ZodFlattenedErrors = {
  formErrors?: string[];
  fieldErrors?: Record<string, string[] | undefined>;
};

function flattenValidationErrors(errors: ZodFlattenedErrors): string | null {
  const parts: string[] = [];

  for (const msg of errors.formErrors ?? []) {
    if (msg) parts.push(msg);
  }

  for (const [field, messages] of Object.entries(errors.fieldErrors ?? {})) {
    for (const msg of messages ?? []) {
      if (msg) parts.push(`${field}: ${msg}`);
    }
  }

  return parts.length ? parts.join(" • ") : null;
}

export function extractApiErrorMessage(err: unknown): string | null {
  const anyErr = err as any;
  const payload = anyErr?.response?.data ?? anyErr?.payload ?? anyErr?.data;

  const detailed =
    payload?.errors && typeof payload.errors === "object"
      ? flattenValidationErrors(payload.errors as ZodFlattenedErrors)
      : null;

  return detailed ?? payload?.message ?? anyErr?.message ?? null;
}
